/**
 * Shared status chip components.
 * Use these anywhere a status, role, or person needs to render inline.
 */
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Archive,
  TrendingUp,
  Lightbulb,
  Ban,
  AlertCircle,
  Minus,
  Target,
} from 'lucide-react';
import type { ProjectStatus, TicketStatus, KrStatus, FunctionalRole } from '../types';
import { Avatar } from './Avatar';

// ── Project status ─────────────────────────────────────────────────────────────

// Solid backgrounds, WCAG AA text contrast
const PROJECT_STATUS_MAP: Record<
  ProjectStatus,
  { label: string; icon: React.ReactNode; classes: string }
> = {
  at_risk:  { label: 'At Risk',   icon: <AlertTriangle className="h-4 w-4" />, classes: 'bg-red-600 text-white' },
  on_track: { label: 'On Track',  icon: <CheckCircle2 className="h-4 w-4" />, classes: 'bg-green-500 text-white' },
  active:   { label: 'Active',    icon: <TrendingUp className="h-4 w-4" />,   classes: 'bg-green-500 text-white' },
  planning: { label: 'Planning',  icon: <Clock className="h-4 w-4" />,        classes: 'bg-blue-500 text-white' },
  draft:    { label: 'Draft',     icon: <Clock className="h-4 w-4" />,        classes: 'bg-gray-400 text-white' },
  complete: { label: 'Complete',  icon: <CheckCircle2 className="h-4 w-4" />, classes: 'bg-teal-600 text-white' },
  archived: { label: 'Archived',  icon: <Archive className="h-4 w-4" />,      classes: 'bg-gray-500 text-white' },
};

export function ProjectStatusChip({ status }: { status: ProjectStatus }) {
  const cfg = PROJECT_STATUS_MAP[status] ?? PROJECT_STATUS_MAP.draft;
  return (
    <span
      className={`inline-flex items-center gap-1 text-meta px-2.5 py-1 rounded-full font-semibold ${cfg.classes}`}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

// ── Ticket status ──────────────────────────────────────────────────────────────

const TICKET_STATUS_MAP: Record<
  TicketStatus,
  { label: string; icon: React.ReactNode; classes: string }
> = {
  triage:   { label: 'Triage',   icon: <AlertCircle className="h-4 w-4" />,  classes: 'bg-orange-500 text-white' },
  at_risk:  { label: 'At Risk',  icon: <AlertTriangle className="h-4 w-4" />, classes: 'bg-red-600 text-white' },
  on_track: { label: 'On Track', icon: <TrendingUp className="h-4 w-4" />,   classes: 'bg-amber-400 text-gray-900' },
  planning: { label: 'Planning', icon: <Clock className="h-4 w-4" />,        classes: 'bg-blue-500 text-white' },
  complete: { label: 'Complete', icon: <CheckCircle2 className="h-4 w-4" />, classes: 'bg-green-500 text-white' },
  idea:     { label: 'Idea',     icon: <Lightbulb className="h-4 w-4" />,    classes: 'bg-purple-500 text-white' },
  invalid:  { label: 'Invalid',  icon: <Ban className="h-4 w-4" />,          classes: 'bg-gray-400 text-white' },
};

export function TicketStatusChip({ status }: { status: TicketStatus }) {
  const cfg = TICKET_STATUS_MAP[status] ?? TICKET_STATUS_MAP.planning;
  return (
    <span
      className={`inline-flex items-center gap-1 text-meta px-2 py-0.5 rounded-full font-semibold ${cfg.classes}`}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

// ── KR / OKR status ───────────────────────────────────────────────────────────

const KR_STATUS_MAP: Record<
  KrStatus,
  { label: string; icon: React.ReactNode; classes: string }
> = {
  not_started: { label: 'Not Started', icon: <Minus className="h-4 w-4" />,       classes: 'bg-gray-400 text-white' },
  in_progress: { label: 'In Progress', icon: <TrendingUp className="h-4 w-4" />,  classes: 'bg-blue-500 text-white' },
  at_risk:     { label: 'At Risk',     icon: <AlertTriangle className="h-4 w-4" />, classes: 'bg-red-600 text-white' },
  achieved:    { label: 'Achieved',    icon: <Target className="h-4 w-4" />,       classes: 'bg-green-500 text-white' },
};

export function KrStatusChip({ status }: { status: KrStatus }) {
  const cfg = KR_STATUS_MAP[status] ?? KR_STATUS_MAP.not_started;
  return (
    <span
      className={`inline-flex items-center gap-1 text-meta px-2 py-0.5 rounded-full font-semibold ${cfg.classes}`}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

// ── Functional role chip ───────────────────────────────────────────────────────

const ROLE_MAP: Record<FunctionalRole, { label: string; classes: string }> = {
  executive:       { label: 'Executive',       classes: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/10 dark:text-violet-300 dark:border-violet-900/20' },
  product_manager: { label: 'Product Manager', classes: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/10 dark:text-sky-300 dark:border-sky-900/20' },
  team_lead:       { label: 'Team Lead',       classes: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/10 dark:text-indigo-300 dark:border-indigo-900/20' },
  engineer:        { label: 'Engineer',        classes: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/10 dark:text-teal-300 dark:border-teal-900/20' },
  designer:        { label: 'Designer',        classes: 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-900/10 dark:text-pink-300 dark:border-pink-900/20' },
  sales:           { label: 'Sales',           classes: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/10 dark:text-amber-300 dark:border-amber-900/20' },
  marketing:       { label: 'Marketing',       classes: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/10 dark:text-orange-300 dark:border-orange-900/20' },
};

export function RoleChip({ role }: { role: FunctionalRole }) {
  const cfg = ROLE_MAP[role];
  if (!cfg) return null;
  return (
    <span
      className={`inline-flex items-center text-meta px-2 py-0.5 rounded-full border font-medium ${cfg.classes}`}
    >
      {cfg.label}
    </span>
  );
}

// ── Person chip — inline avatar + name ───────────────────────────────────────

export function PersonChip({
  name,
  pictureUrl,
}: {
  name: string;
  pictureUrl?: string | null;
}) {
  return (
    <span className="inline-flex items-center gap-1 text-meta px-2 py-0.5 rounded-full border font-medium bg-gray-50 text-gray-700 border-gray-200 dark:bg-white/[0.06] dark:text-gray-300 dark:border-white/10">
      <Avatar
        user={{ fullName: name, profilePictureUrl: pictureUrl ?? null }}
        size="sm"
      />
      {name}
    </span>
  );
}
